/**
 * Icon — self-hosted SVG icon for the Mango — Farmer Market kit (TD-093).
 *
 * Renders an inline `<svg>` from the tree-shaken `icons.generated.ts` registry
 * (Material Symbols Rounded, weight 500 — outlined + filled variants), which
 * replaces the 5.3 MB Material Symbols webfont. No Google CDN (POPIA-safe), no
 * font-load flash, and only the icons the app actually uses are bundled.
 *
 * Per the design fileset: icons pair with a text label, never emoji, and
 * inherit colour via `currentColor` so contrast follows context.
 *
 * Add a new icon: extend `ICON_NAMES` in `scripts/gen-icons.mjs` and re-run it
 * (`node scripts/gen-icons.mjs` from packages/ui). An unregistered name renders
 * nothing and logs a one-time console warning (never a silent tofu glyph).
 *
 * This is the canonical icon wrapper — other kit components compose it.
 */
import { forwardRef } from 'react';

import { ICONS, type IconName } from './icons.generated.js';

export interface IconProps {
  /** Material Symbols name, e.g. `storefront`, `check_circle`. Must be in the
   *  registry (`icons.generated.ts` — see `scripts/gen-icons.mjs`). */
  name: IconName | (string & {});
  /** Icon size in px. Default 24. */
  size?: number;
  /** Filled vs outlined variant. Default false (outlined). */
  fill?: boolean;
  /** @deprecated The self-hosted set ships weight 500 only — this is ignored. */
  weight?: number;
  /** Override colour. Defaults to `currentColor` so it inherits from context. */
  color?: string;
  /** Pass-through className for layout composition. */
  className?: string;
  /** Accessible label. When omitted the icon is hidden from assistive tech. */
  'aria-label'?: string;
}

// Warn at most once per unknown name so a missing icon surfaces in dev without
// spamming the console on every re-render.
const warned = new Set<string>();

export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { name, size = 24, fill = false, color, className, 'aria-label': ariaLabel },
  ref,
) {
  const entry = ICONS[name as IconName];
  if (!entry) {
    if (!warned.has(name)) {
      warned.add(name);
      // eslint-disable-next-line no-console
      console.warn(
        `[@fse/ui] <Icon name="${name}"> is not in the icon registry — add it to ` +
          `scripts/gen-icons.mjs and regenerate (TD-093).`,
      );
    }
    return null;
  }

  const paths = fill && entry.f ? entry.f : entry.o;
  return (
    <svg
      ref={ref}
      className={className}
      width={size}
      height={size}
      viewBox="0 -960 960 960"
      fill="currentColor"
      aria-label={ariaLabel}
      aria-hidden={ariaLabel ? undefined : true}
      role={ariaLabel ? 'img' : undefined}
      style={{
        color: color ?? 'currentColor',
        display: 'inline-block',
        verticalAlign: 'middle',
        flexShrink: 0,
        userSelect: 'none',
      }}
    >
      {paths.map((d, i) => (
        <path key={i} d={d} />
      ))}
    </svg>
  );
});
